import { useState, useEffect } from 'react';
import { X, ArrowDownUp, Loader2 } from 'lucide-react';
import { api } from '../../api/client';

type ConversionSource =
  | { type: 'amazon_purchase'; purchase_id: number; ml_order_id?: string }
  | { type: 'manual_purchase'; purchase_id: number }
  | { type: 'manual_inventory' };

interface CbtItem { item_id: string; title: string; }
interface Supplier { id: number; name: string; }

export interface ConvertToInternalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result: any) => void;
  // Seller dueño de la compra/depósito. Sin esto el backend cae al seller 1 y la
  // conversión va al depósito equivocado (bug real 2026-08-01, seller 8).
  sellerId: number;
  stockProductId: number;
  productTitle: string;
  productAsin: string | null;
  qtyOnHand: number;
  defaultQty?: number;
  lockQty?: boolean;
  defaultUnitCost?: number;
  source: ConversionSource;
  defaultNotes?: string;
}

export function ConvertToInternalModal(props: ConvertToInternalModalProps) {
  const {
    isOpen, onClose, onSuccess, sellerId,
    stockProductId, productTitle, productAsin, qtyOnHand,
    defaultQty = 1, lockQty = false, defaultUnitCost = 0,
    source, defaultNotes = '',
  } = props;

  const [qty, setQty] = useState(defaultQty);
  const [unitCost, setUnitCost] = useState(defaultUnitCost);
  const [supplierId, setSupplierId] = useState<number | ''>('');
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [internalSku, setInternalSku] = useState('');
  const [cbtItems, setCbtItems] = useState<CbtItem[]>([]);
  const [cbtItemId, setCbtItemId] = useState('');
  const [notes, setNotes] = useState(defaultNotes);
  const [pricePreview, setPricePreview] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when opening with new props
  useEffect(() => {
    if (isOpen) {
      setQty(defaultQty);
      setUnitCost(defaultUnitCost);
      setNotes(defaultNotes);
      setError(null);
      setInternalSku('');
    }
  }, [isOpen, defaultQty, defaultUnitCost, defaultNotes]);

  // Load suppliers + CBT items on open
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const supRes = await api.get<Supplier[]>(`/suppliers?seller_id=${sellerId}`);
        if (supRes.success && supRes.data) {
          setSuppliers(supRes.data);
          const residual = supRes.data.find(s => s.name === 'Amazon (residual)');
          if (residual) setSupplierId(residual.id);
        }
      } catch { /* no suppliers loaded */ }

      if (productAsin) {
        try {
          const cbtRes = await api.get<CbtItem[]>(`/internal-stock/ml-links?asin=${productAsin}&seller_id=${sellerId}`);
          if (cbtRes.success && cbtRes.data) {
            setCbtItems(cbtRes.data);
            if (cbtRes.data.length > 0) setCbtItemId(cbtRes.data[0].item_id);
          }
        } catch { /* no cbt items */ }
      }
    })();
  }, [isOpen, productAsin]);

  // Real-time markup preview
  useEffect(() => {
    if (!isOpen || unitCost <= 0) { setPricePreview(null); return; }
    const timeout = setTimeout(async () => {
      try {
        const res = await api.get<{ ml_price: number; error?: string | null }>(
          `/internal-stock/markup-preview?cost=${unitCost}&cbt_item_id=${cbtItemId}&seller_id=${sellerId}`
        );
        if (res.success && res.data) setPricePreview(res.data.ml_price);
      } catch { setPricePreview(null); }
    }, 300);
    return () => clearTimeout(timeout);
  }, [unitCost, cbtItemId, isOpen]);

  async function handleSubmit() {
    setError(null);
    setLoading(true);
    try {
      const res = await api.post<any>('/internal-stock/convert', {
        seller_id: sellerId,
        stock_product_id: stockProductId,
        qty,
        unit_cost: unitCost,
        supplier_id: supplierId || null,
        internal_sku: internalSku || null,
        cbt_item_id: cbtItemId || null,
        source,
        notes: notes || null,
      });
      if (!res.success) throw new Error(res.error || 'Error al convertir');
      onSuccess(res.data);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error al convertir');
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-white dark:bg-gray-800 p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ArrowDownUp className="w-5 h-5 text-cyan-600" />
            <h2 className="text-lg font-bold">Convertir a Stock Interno</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded text-sm">
            <div className="font-semibold">{productTitle}</div>
            <div className="text-xs text-gray-500 mt-1">
              ASIN: {productAsin ?? '—'} · On hand: {qtyOnHand}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium">Cantidad</span>
              {/* Rescate desde compra Amazon (orden cancelada): puede no haber stock
                  residual recepcionado (on hand 0) — el backend auto-recibe. No topear
                  a qtyOnHand en ese caso (topear a 0 dejaba el botón muerto). */}
              <input
                type="number" min={1}
                max={source.type === 'amazon_purchase' ? undefined : qtyOnHand}
                disabled={lockQty}
                value={qty}
                onChange={e => {
                  const v = Math.max(1, +e.target.value);
                  setQty(source.type === 'amazon_purchase' ? v : Math.min(v, qtyOnHand));
                }}
                className="mt-1 w-full rounded border px-3 py-2 dark:bg-gray-900 disabled:opacity-60"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Costo unitario (USD)</span>
              <input
                type="number" step="0.01" min={0.01}
                value={unitCost}
                onChange={e => setUnitCost(+e.target.value)}
                className="mt-1 w-full rounded border px-3 py-2 dark:bg-gray-900"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-sm font-medium">Proveedor</span>
            <select
              value={supplierId}
              onChange={e => setSupplierId(e.target.value ? +e.target.value : '')}
              className="mt-1 w-full rounded border px-3 py-2 dark:bg-gray-900"
            >
              <option value="">— Sin supplier —</option>
              {suppliers.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-sm font-medium">SKU interno (opcional)</span>
            <input
              type="text"
              placeholder={`auto: INT-${productAsin ?? 'MNL'}-YYYYMMDD`}
              value={internalSku}
              onChange={e => setInternalSku(e.target.value)}
              className="mt-1 w-full rounded border px-3 py-2 dark:bg-gray-900"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium">Listar en ML</span>
            <select
              value={cbtItemId}
              onChange={e => setCbtItemId(e.target.value)}
              className="mt-1 w-full rounded border px-3 py-2 dark:bg-gray-900"
            >
              <option value="">— No listar (solo convertir) —</option>
              {cbtItems.map(it => (
                <option key={it.item_id} value={it.item_id}>
                  {it.item_id} — {it.title.slice(0, 50)}
                </option>
              ))}
            </select>
          </label>

          {pricePreview !== null && pricePreview > 0 && (
            <div className="bg-cyan-50 dark:bg-cyan-900/20 p-3 rounded text-sm">
              <span className="text-gray-600 dark:text-gray-400">Precio ML calculado:</span>
              <span className="ml-2 font-semibold text-cyan-700 dark:text-cyan-300">
                ${pricePreview.toFixed(2)}
              </span>
            </div>
          )}

          <label className="block">
            <span className="text-sm font-medium">Notas (opcional)</span>
            <textarea
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="mt-1 w-full rounded border px-3 py-2 dark:bg-gray-900"
            />
          </label>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 p-3 rounded text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={onClose} disabled={loading}
              className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || unitCost <= 0 || qty <= 0}
              className="px-4 py-2 rounded bg-cyan-600 hover:bg-cyan-700 text-white disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Convertir y Listar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
